import { defineStore } from "pinia"
import { ref, computed } from "vue"

export const useShipperStore = defineStore("shipper", {
  state: () => ({
    shipments: [],
    selectedShipmentId: null,
    isLoading: false,
    error: null,
    metrics: null,
    activeShipments: [],
    completedShipments: [],
    pendingShipments: [],
    shipmentRequests: [],
    availableDrivers: [],
    availableCarriers: [],
    availableForwarders: [],
    forwarderRequests: [],
    vehicleTracking: {}, // Store vehicle GPS tracking data { vehicleId: trackingData }
    driverLocations: {}, // Store driver phone locations { driverId: locationData }
  }),

  getters: {
    allAvailableDrivers: (state) => {
      this.getAvailableDrivers()
      return state.availableDrivers
    },

    selectedShipment: (state) => {
      if (!state.selectedShipmentId) {
        // Default to first active shipment (in_transit, accepted, or pending)
        const activeShipment = state.shipments.find((s) => ["in_transit", "accepted", "pending"].includes(s.status))
        return activeShipment || state.shipments[0] // Fallback to first shipment if no active ones
      }
      return state.shipments.find((s) => s.id === state.selectedShipmentId) || state.shipments[0]
    },

    stats: (state) => {
      const total = state.shipments.length
      const enroute = state.shipments.filter((s) => s.status === "in transit").length
      const completed = state.shipments.filter((s) => s.status === "delivered").length
      const pending = state.shipments.filter((s) => s.status === "pending").length

      return {
        total: {
          count: total,
          change: (((total - 165) / 165) * 100).toFixed(2),
          increasing: total > 165,
        },
        enroute: {
          count: enroute,
          change: (((enroute - 22) / 22) * 100).toFixed(2),
          increasing: enroute > 22,
        },
        completed: {
          count: completed,
          change: (((completed - 62) / 62) * 100).toFixed(2),
          increasing: completed > 62,
        },
        pending: {
          count: pending,
          change: (((pending - 4) / 4) * 100).toFixed(2),
          increasing: pending > 4,
        },
      }
    },

    shipmentOptions: (state) => {
      return state.shipments.map((shipment) => ({
        id: shipment.id,
        label: shipment.tracking_id,
        value: shipment.id,
        tracking_id: shipment.tracking_id,
        status: shipment.status,
        formatted_price: shipment.formatted_price,
      }))
    },

    selectedShipmentWithDriver: (state) => {
      const shipment = state.selectedShipment
      if (!shipment) return null

      return {
        ...shipment,
        driver: transformDriverForMap(shipment),
      }
    },

    selectedShipmentWithVehicle: (state) => {
      const shipment = state.selectedShipment
      if (!shipment) return null

      return {
        ...shipment,
        vehicle: transformVehicleForMap(shipment),
      }
    },

    // Get tracking data for a specific vehicle (GPS + driver phone fallback)
    getVehicleTrackingData: (state) => (vehicleId) => {
      return state.vehicleTracking[vehicleId] || null
    },

    // Get combined tracking for shipment (prioritizes GPS, falls back to driver phone)
    getShipmentTrackingData: (state) => {
      return (shipmentId) => {
        if (!state.shipments || state.shipments.length === 0) return null

        const shipment = state.shipments.find((s) => s.id === shipmentId)
        if (!shipment) return null

        const vehicleId = shipment.vehicle?.id
        const driverId = shipment.driver?.id

        // Priority: Vehicle GPS > Driver Phone > Last Known
        const vehicleGPS = vehicleId ? state.vehicleTracking[vehicleId] : null
        const driverPhone = driverId ? state.driverLocations[driverId] : null

        if (vehicleGPS && vehicleGPS.last_update) {
          return {
            source: "gps_device",
            location: vehicleGPS.location,
            timestamp: vehicleGPS.last_update,
            reliability: vehicleGPS.reliability || 95,
            signalStrength: vehicleGPS.signal_strength,
          }
        }

        if (driverPhone && driverPhone.lat && driverPhone.lng) {
          return {
            source: "phone",
            location: {
              latitude: driverPhone.lat,
              longitude: driverPhone.lng,
            },
            timestamp: driverPhone.timestamp || new Date().toISOString(),
            reliability: 75,
            signalStrength: driverPhone.accuracy || 50,
          }
        }

        // Last known location
        if (shipment.current_location) {
          return {
            source: "last_known",
            location: {
              latitude: parseFloat(shipment.current_location.lat),
              longitude: parseFloat(shipment.current_location.lng),
            },
            timestamp: shipment.updated_at,
            reliability: 30,
            signalStrength: 0,
          }
        }

        return null
      }
    },
  },

  actions: {
    async getAvailableDrivers() {
      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/fleet/drivers/available`)
        this.availableDrivers = response.data.data
      } catch (error) {
        // console.error("Error fetching available drivers:", error)
        this.error = error.response?.data?.message || "Failed to fetch available drivers"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },
    async getAvailableCarriers(params = {}) {
      try {
        this.isLoading = true
        const response = await this.$axios.get(`/api/v1/forwarder/carriers/available`, { params })
        this.availableCarriers = response.data?.data || []
        return { success: true, data: this.availableCarriers }
      } catch (error) {
        if (error.response?.status === 404) {
          this.availableCarriers = []
          return { success: true, data: [] }
        }
        this.error = error.response?.data?.message || "Failed to fetch available carriers"
        return { success: false, error: this.error, data: [] }
      } finally {
        this.isLoading = false
      }
    },
    async getShipmentRequests() {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/shipment/cargo-requests`)
        this.shipmentRequests = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to get shipment requests"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },
    async updateShipmentRequest(requestId, requestData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/shipment/cargo-requests/${requestId}`, requestData)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update shipment request"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async createShipmentRequest(shipmentData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/shipment/cargo-requests", shipmentData)
        await this.getShipmentRequests() // Refresh the list
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create shipment request"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async uploadCargoRequestDocument(requestId, payload, config = {}) {
      try {
        const response = await this.$axios.post(`/api/v1/shipment/cargo-requests/${requestId}/documents/upload`, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(config.headers || {}),
          },
          ...config,
        })

        return {
          success: true,
          data: response.data?.data || response.data,
          message: response.data?.message || "Document uploaded successfully",
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to upload cargo request document"
        this.error = errorMessage
        return {
          success: false,
          error: errorMessage,
          errors: error.response?.data?.errors || null,
        }
      }
    },

    async getCargoRequestDocuments(requestId) {
      try {
        const response = await this.$axios.get(`/api/v1/shipment/cargo-requests/${requestId}/documents`)
        return {
          success: true,
          data: response.data?.data || [],
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to fetch cargo request documents"
        this.error = errorMessage
        return {
          success: false,
          error: errorMessage,
          data: [],
        }
      }
    },

    async deleteCargoRequestDocument(requestId, documentId) {
      try {
        const response = await this.$axios.delete(`/api/v1/shipment/cargo-requests/${requestId}/documents/${documentId}`)
        return {
          success: true,
          data: response.data?.data || null,
          message: response.data?.message || "Document deleted successfully",
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to delete cargo request document"
        this.error = errorMessage
        return {
          success: false,
          error: errorMessage,
        }
      }
    },

    async submitForwarderRequestOffer(requestId, payload) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/forwarder/requests/${requestId}/offers`, payload)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to submit offer"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async acceptForwarderRequest(requestId, payload) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/forwarder/requests/${requestId}/shipper/accept`, payload)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to accept forwarder request"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async declineForwarderRequest(requestId, payload) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/forwarder/requests/${requestId}/shipper/decline`, payload)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to decline forwarder request"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    // Enhanced Forwarder Workflow Methods for Shipper

    async getForwarderNegotiationHistory(requestId) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/forwarder/requests/${requestId}/negotiation-history`)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch negotiation history"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },
    async startForwarderNegotiation(requestId, negotiationData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/forwarder/requests/${requestId}/negotiate`, negotiationData)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to start negotiation"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async addForwarderNegotiation(requestId, negotiationData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/forwarder/requests/${requestId}/negotiation`, negotiationData)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to add negotiation"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async acceptForwarderFinalPrice(requestId, payload) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/forwarder/requests/${requestId}/accept`, payload)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to accept final price"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async declineForwarderRequestWorkflow(requestId, payload) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/forwarder/requests/${requestId}/decline`, payload)
        return { success: true, data: response.data.data, message: response.data.message }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to decline forwarder request"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },
    async fetchShipments() {
      try {
        this.isLoading = true
        this.error = null
        // Using this.$axios injected by pinia plugin

        const response = await this.$axios.get("/api/v1/shipment/shipments")
        this.shipments = response.data.data

        // Update filtered lists
        this.updateShipmentLists()

        // Set default selected shipment if none is selected
        if (!this.selectedShipmentId && this.shipments.length > 0) {
          const activeShipment = this.shipments.find((s) => ["in_transit", "accepted", "pending"].includes(s.status))
          this.selectedShipmentId = activeShipment?.id || this.shipments[0].id
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        // console.error("Error fetching shipments:", error)
        this.error = error.response?.data?.message || "Failed to fetch shipments"

        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async updateShipment(shipmentId, shipmentData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/shipper/shipments/${shipmentId}`, shipmentData)
        await this.fetchShipments() // Refresh the list
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update shipment"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async cancelShipment(shipmentId, reason) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipper/shipments/${shipmentId}/cancel`, { reason })
        await this.fetchShipments() // Refresh the list
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to cancel shipment"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async confirmDelivery(shipmentId, confirmationData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipper/shipments/${shipmentId}/confirm-delivery`, confirmationData)
        await this.fetchShipments() // Refresh the list
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to confirm delivery"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async rateDriver(shipmentId, ratingData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipper/shipments/${shipmentId}/rate-driver`, {
          rating: ratingData.rating,
          comment: ratingData.comment,
          categories: ratingData.categories, // e.g., ['punctuality', 'professionalism', 'communication']
        })
        await this.fetchShipments() // Refresh the list
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to submit driver rating"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async reportShipmentIssue(shipmentId, issueData) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipper/shipments/${shipmentId}/report-issue`, {
          type: issueData.type,
          description: issueData.description,
          severity: issueData.severity,
          attachments: issueData.attachments,
        })
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to report issue"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async getShipmentTracking(shipmentId) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/shipper/shipments/${shipmentId}/tracking`)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to get tracking updates"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async getShipmentTimeline(shipmentId) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/shipper/shipments/${shipmentId}/timeline`)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to get shipment timeline"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    // Vehicle GPS Tracking Methods
    async fetchVehicleTracking(vehicleId) {
      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/tracking/vehicles/${vehicleId}/tracking`)

        // Update state with GPS tracking data
        this.vehicleTracking[vehicleId] = {
          location: response.data.data.location,
          last_update: response.data.data.last_update,
          device_id: response.data.data.device_id,
          reliability: response.data.data.reliability || 95,
          signal_strength: response.data.data.signal_strength || 100,
          battery_level: response.data.data.battery_level,
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        console.warn("Failed to fetch vehicle GPS tracking:", error)
        return { success: false, error: error.response?.data?.message || "GPS tracking unavailable" }
      }
    },

    async fetchVehicleTrackingHistory(vehicleId, filters = {}) {
      try {
        // Using this.$axios injected by pinia plugin
        const params = new URLSearchParams(filters).toString()
        const response = await this.$axios.get(`/api/v1/tracking/vehicles/${vehicleId}/history?${params}`)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch tracking history"
        return { success: false, error: this.error }
      }
    },

    // Update driver location (from WebSocket or API)
    updateDriverLocation(driverId, locationData) {
      this.driverLocations[driverId] = {
        lat: locationData.lat || locationData.latitude,
        lng: locationData.lng || locationData.longitude,
        timestamp: locationData.timestamp || new Date().toISOString(),
        accuracy: locationData.accuracy || 50,
        speed: locationData.speed,
        heading: locationData.heading,
      }
    },

    // Update vehicle GPS data (from WebSocket or API)
    updateVehicleTracking(vehicleId, trackingData) {
      this.vehicleTracking[vehicleId] = {
        location: trackingData.location,
        last_update: trackingData.timestamp || new Date().toISOString(),
        device_id: trackingData.device_id,
        reliability: trackingData.reliability || 95,
        signal_strength: trackingData.signal_strength || 100,
        battery_level: trackingData.battery_level,
      }
    },
    async getAvailableForwarders() {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/forwarder/available`)
        this.availableForwarders = response.data.data
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch available forwarders"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    setSelectedShipment(id) {
      this.selectedShipmentId = id
    },

    updateShipmentLists() {
      this.activeShipments = this.shipments.filter((s) => s.status === "in transit")
      this.completedShipments = this.shipments.filter((s) => s.status === "delivered")
      this.pendingShipments = this.shipments.filter((s) => s.status === "pending")
    },

    clearError() {
      this.error = null
    },

    reset() {
      this.shipments = []
      this.selectedShipmentId = null
      this.isLoading = false
      this.error = null
      this.metrics = null
      this.activeShipments = []
      this.completedShipments = []
      this.pendingShipments = []
      this.shipmentRequests = []
      this.availableDrivers = []
      this.availableCarriers = []
      this.availableForwarders = []
      this.vehicleTracking = {}
      this.driverLocations = {}
    },

    async createCityXpressRequest(data) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/cityxpress/requests", data)

        if (response.data.status === "success") {
          // Refresh shipments list to include the new request
          await this.fetchShipments()
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to create CityXpress request"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      } finally {
        this.isLoading = false
      }
    },

    async getCityXpressRequest(id) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/cityxpress/requests/${id}`)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch CityXpress request"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async getCityXpressRequestByNumber(requestNumber) {
      try {
        this.isLoading = true
        const response = await this.$axios.get(`/api/v1/cityxpress/requests/by-number/${requestNumber}`)
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch CityXpress request"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    async cancelCityXpressRequest(id, reason = null) {
      try {
        this.isLoading = true
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/cityxpress/requests/${id}/cancel`, { reason })
        await this.fetchShipments() // Refresh the list
        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to cancel CityXpress request"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    // Backward compatibility - keep old method name
    async createQuickShipment(data) {
      // Alias to createCityXpressRequest for backward compatibility
      return await this.createCityXpressRequest(data)
    },

    async getCityXpressPricing(vehicleTypeId, distanceKm) {
      try {
        const response = await this.$axios.get("/api/v1/pricing/cityxpress", {
          params: {
            vehicle_type_id: vehicleTypeId,
            distance_km: distanceKm,
          },
        })
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to calculate CityXpress pricing"
        return { success: false, error: this.error }
      }
    },

    async getCityXpressAllPricing(distanceKm) {
      try {
        const response = await this.$axios.get("/api/v1/pricing/cityxpress/all", {
          params: {
            distance_km: distanceKm,
          },
        })
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch CityXpress pricing"
        return { success: false, error: this.error }
      }
    },

    async getCityXpressHistory(limit = 20, offset = 0) {
      try {
        const response = await this.$axios.get("/api/v1/cityxpress/requests/history", {
          params: { limit, offset },
        })
        return { success: true, data: response.data.data || [], total: response.data.total || 0 }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch CityXpress history"
        return { success: false, error: this.error }
      }
    },

    async rateCityXpressRequest(requestId, rating, comment = "") {
      try {
        const response = await this.$axios.post(`/api/v1/cityxpress/requests/${requestId}/rate`, {
          rating,
          comment,
        })
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to submit rating"
        return { success: false, error: this.error }
      }
    },

    async getCityXpressAvailability(pickupLat, pickupLng) {
      try {
        const response = await this.$axios.get("/api/v1/cityxpress/availability", {
          params: {
            pickup_lat: pickupLat,
            pickup_lng: pickupLng,
          },
        })
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch CityXpress availability"
        return { success: false, error: this.error }
      }
    },

    async uploadCityXpressRequestDocument(requestId, payload, config = {}) {
      try {
        const response = await this.$axios.post(`/api/v1/cityxpress/requests/${requestId}/documents/upload`, payload, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(config.headers || {}),
          },
          ...config,
        })

        return {
          success: true,
          data: response.data?.data || response.data,
          message: response.data?.message || "Document uploaded successfully",
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to upload CityXpress document"
        return {
          success: false,
          error: this.error,
          errors: error.response?.data?.errors || null,
        }
      }
    },

    async getCityXpressRequestDocuments(requestId) {
      try {
        const response = await this.$axios.get(`/api/v1/cityxpress/requests/${requestId}/documents`)
        return {
          success: true,
          data: response.data?.data || [],
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch CityXpress documents"
        return {
          success: false,
          error: this.error,
          data: [],
        }
      }
    },

    async deleteCityXpressRequestDocument(requestId, documentId) {
      try {
        const response = await this.$axios.delete(`/api/v1/cityxpress/requests/${requestId}/documents/${documentId}`)
        return {
          success: true,
          data: response.data?.data || null,
          message: response.data?.message || "Document deleted successfully",
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to delete CityXpress document"
        return {
          success: false,
          error: this.error,
        }
      }
    },

    async counterCityXpressMoverOffer(requestId, payload) {
      try {
        const response = await this.$axios.post(`/api/v1/cityxpress/requests/${requestId}/movers/counter`, payload)
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to submit movers counter offer"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      }
    },

    async acceptCityXpressMoverOffer(requestId, moverOfferId) {
      try {
        const response = await this.$axios.post(`/api/v1/cityxpress/requests/${requestId}/movers/accept`, {
          mover_offer_id: moverOfferId,
        })
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to accept movers offer"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      }
    },

    async declineCityXpressMoverOffer(requestId, moverOfferId, reason = null) {
      try {
        const response = await this.$axios.post(`/api/v1/cityxpress/requests/${requestId}/movers/decline`, {
          mover_offer_id: moverOfferId,
          reason,
        })
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to decline movers offer"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      }
    },

    async updateCityXpressMoversStage(requestId, payload) {
      try {
        const response = await this.$axios.post(`/api/v1/cityxpress/requests/${requestId}/movers/stage`, payload)
        return { success: true, data: response.data.data || response.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update movers stage"
        return { success: false, error: this.error, errors: error.response?.data?.errors || null }
      }
    },

    // Helper method to update shipment options
    updateShipmentOptions() {
      this.shipmentOptions = this.shipments.map((shipment) => ({
        id: shipment.id,
        label: `${shipment.id} - ${shipment.pickup_address} to ${shipment.dropoff_address}`,
      }))
    },
  },
})

// Helper function to transform driver data for LiveMap
function transformDriverForMap(shipment) {
  if (!shipment?.driver) return null

  return {
    id: shipment.driver.id,
    name: shipment.driver.name,
    avatar: shipment.driver.avatar,
    details: {
      vehicles: [
        {
          id: shipment.vehicle?.id, // Include vehicle ID for GPS tracking
          make: shipment.vehicle?.make,
          model: shipment.vehicle?.model,
          plate_number: shipment.vehicle?.plate || shipment.vehicle?.plate_number,
          vehicle_type: shipment.vehicle?.type || shipment.vehicle?.vehicle_type,
          gps_device_id: shipment.vehicle?.gps_device_id, // GPS device identifier
        },
      ],
      status: shipment.status,
      metrics: {
        average_rating: shipment.driver?.rating,
      },
    },
    location: {
      coordinates: shipment.location?.coordinates,
      current: shipment.location?.current,
      isLive: shipment.status === "in_transit" || shipment.status === "accepted",
      lastActive: shipment.updated_at || new Date().toISOString(),
    },
  }
}

// Helper function to transform vehicle data for LiveMap
function transformVehicleForMap(shipment) {
  if (!shipment?.vehicle) return null

  return {
    id: shipment.vehicle.id,
    plate_number: shipment.vehicle.plate_number,
    make: shipment.vehicle.make,
    model: shipment.vehicle.model,
    year: shipment.vehicle.year,
    vehicle_type: shipment.vehicle.vehicle_type,
    capacity: shipment.vehicle.capacity,
    status: shipment.vehicle.status,
    details: {
      carrier: {
        company_name: shipment.carrier.company_name,
        contact_phone: shipment.carrier.contact_phone,
      },
      cargo: {
        type: shipment.cargo_request.cargo_type.name,
        category: shipment.cargo_request.cargo_category.name,
        weight: shipment.cargo_request.total_weight,
        weight_unit: shipment.cargo_request.weight_unit,
      },
      status: shipment.status,
    },
    location: {
      coordinates: shipment.current_location
        ? {
            lat: parseFloat(shipment.current_location.lat),
            lng: parseFloat(shipment.current_location.lng),
          }
        : null,
      current: shipment.current_location
        ? {
            address: shipment.current_location.address,
            details: shipment.current_location.details,
          }
        : null,
      isLive: shipment.status === "in_transit",
      lastActive: shipment.updated_at,
    },
  }
}
